/**
 * Multi-Factor Authentication Interfaces
 */

/**
 * Authenticator as returned by the API (public view, no secrets)
 * Note: name is no longer returned by the API (auto-generated UUID stored internally)
 */
export interface Authenticator {
  id: string;
  created_at: string;
  last_used_at: string | null;
}

/**
 * Organization that requires MFA
 */
export interface MFARequiredOrganization {
  id: string;
  name: string;
}

/**
 * Response when starting to add a new authenticator
 */
export interface MultiFactorSetupResponse {
  id: string;
  totp_uri: string;
  secret_for_manual_entry: string;
}

/**
 * Response after verifying and activating an authenticator
 */
export interface MultiFactorVerifyAuthenticatorResponse {
  authenticator: Authenticator;
  backup_codes?: string[];
}

/**
 * Response when generating new backup codes
 */
export interface BackupCodesResponse {
  backup_codes: string[];
}

/**
 * Response for backup codes count
 */
export interface BackupCodesCountResponse {
  count: number;
}

/**
 * Response for MFA status
 */
export interface MultiFactorStatusResponse {
  enabled: boolean;
  authenticator_count: number;
  backup_codes_count: number;
}

/**
 * Response when MFA verification is required during login
 */
export interface MultiFactorRequiredResponse {
  multi_factor_required: true;
  multi_factor_token: string;
}

/**
 * Organization info with MFA requirement status
 */
export interface OrganizationWithMFAStatus {
  id: string;
  name: string;
  mfa_required: boolean;
}

/**
 * Standard login response (when no MFA)
 */
export interface LoginSuccessResponse {
  redirect: string;
}

/**
 * Combined login response type
 */
export type LoginResponse = LoginSuccessResponse | MultiFactorRequiredResponse;

/**
 * Type guard to check if login response requires MFA verification
 */
export function isMultiFactorRequired(
  response: LoginResponse,
): response is MultiFactorRequiredResponse {
  return 'multi_factor_required' in response && response.multi_factor_required === true;
}

/**
 * Per-organization MFA compliance status from /session/mfa-compliance.
 */
export interface MFAComplianceStatus {
  is_compliant: boolean;
  organization: OrganizationWithMFAStatus;
  /** All organizations the user belongs to, used by the UI to offer switching to a non-MFA-requiring org. */
  all_organizations: OrganizationWithMFAStatus[];
}
